import { useEffect, useCallback, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import getClientsByFirm from "../../../apis/getClientsByFirm";
import getRecommendations from "../../../apis/getRecommendations";
import sendRecommendation from "../../../apis/sendRecommendation";
import { useAuthContext } from "../../../context/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RecommendModal = ({
  show,
  handleClose,
  selectedClients,
  setSelectedClients,
  resourceSlug,
}) => {
  const { userDetails } = useAuthContext();
  const user_slug = JSON.parse(userDetails).user_slug;
  const firm_id = JSON.parse(userDetails).firm_details?.firm_id;

  const [clientList, setClientList] = useState([]);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [responseType, setResponseType] = useState(null);
  const [recommendedClientSlugs, setRecommendedClientSlugs] = useState([]);

  const handleModalClose = () => {
    setErrors({});
    setSubject("");
    setContent("");
    setSelectedClients([]);
    handleClose();
  };

  const getRecommendedClients = useCallback(() => {
    getRecommendations(resourceSlug)
      .then((res) => {
        if (res?.data?.status === 1 && Array.isArray(res.data?.ids)) {
          const slugs = res.data.ids.map((slug) => slug.trim());
          setRecommendedClientSlugs(slugs);
        } else {
          setRecommendedClientSlugs([]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch recommendations:", err);
        setRecommendedClientSlugs([]);
      });
  }, [resourceSlug]);

  const getClients = useCallback(() => {
    getClientsByFirm(user_slug, firm_id)
      .then((res) => {
        if (res?.data?.status === 1 && Array.isArray(res.data?.clients)) {
          const formattedClients = res?.data?.clients.map((client, index) => ({
            id: index + 1,
            name: client.client_name,
            slug: client.user_slug,
          }));
          setClientList(formattedClients);
        } else {
          setClientList([]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch clients:", err);
        setClientList([]);
      });
  }, [user_slug, firm_id]);

  useEffect(() => {
    getRecommendedClients();
    getClients();
  }, [getClients, getRecommendedClients]);

  const handleSend = async () => {
    const validationErrors = {};

    if (selectedClients.length === 0) {
      validationErrors.clients = "Please select at least one client.";
    }
    if (!subject.trim()) {
      validationErrors.subject = "Subject is required.";
    }
    if (!content.trim()) {
      validationErrors.content = "Message content is required.";
    }

    setErrors(validationErrors);
    setResponseMessage(null);

    if (Object.keys(validationErrors).length === 0) {
      try {
        setSending(true);

        const formData = {
          user_slug: user_slug,
          subject: subject,
          content: content,
          clients: selectedClients,
          resource_slug: resourceSlug,
        };

        sendRecommendation(formData)
          .then((res) => {
            if (res?.data?.status === 1) {
              toast.success(
                res?.data?.message || "Recommendation sent successfully!"
              );
              const slugs = res.data.ids.map((slug) => slug.trim());
              setRecommendedClientSlugs(slugs);
              setSelectedClients([]);
              setSubject("");
              setContent("");
              setErrors({});
              handleClose();
            } else {
              setResponseType("error");
              setResponseMessage(
                res?.data?.message || "Failed to send recommendation."
              );
            }
          })
          .catch((err) => {
            setResponseType("error");
            setResponseMessage(err.message);
          });
      } catch (err) {
        setResponseType("error");
        setResponseMessage("Failed to send recommendation. Please try again.");
      } finally {
        setSending(false);
      }
    }
  };

  const toggleClient = (clientSlug) => {
    setSelectedClients((prev) =>
      prev.includes(clientSlug)
        ? prev.filter((slug) => slug !== clientSlug)
        : [...prev, clientSlug]
    );
  };

  const getClientName = (slug) => {
    const client = clientList.find((c) => c.slug === slug);
    return client ? client.name : slug;
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size=""
      centered
      scrollable
      backdrop="static"
      animation={false}
    >
      <Modal.Header className="d-flex justify-content-between align-items-center">
        <Modal.Title className="fw-bold-custom">
          Send Recommendation
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleModalClose}
          disabled={sending}
        ></button>
      </Modal.Header>

      <Modal.Body>
        {responseMessage && (
          <div
            className={`alert mb-3 ${
              responseType === "success" ? "alert-success" : "alert-danger"
            }`}
            role="alert"
          >
            {responseMessage}
          </div>
        )}
        {/* Send To Field */}
        <div className="form-control d-flex flex-wrap align-items-center gap-2 bg-light">
          {selectedClients.length === 0 && (
            <span className="text-muted" style={{ userSelect: "none" }}>
              Select clients from below list
            </span>
          )}

          {selectedClients.map((clientSlug) => (
            <span
              key={clientSlug}
              className="badge bg-primary text-white"
              style={{
                fontSize: "0.9rem",
                fontWeight: "400",
                padding: "0.5em 1rem",
                borderRadius: "1.2rem",
                userSelect: "none",
                cursor: "default",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {getClientName(clientSlug)}
            </span>
          ))}
        </div>

        {/* Select Clients */}
        <div className="mb-3">
          <label className="form-label fw-semibold mt-1">
            <strong>Select Client(s)</strong>
          </label>
          {clientList.length === 0 ? (
            <div className="alert alert-info mb-0 py-2 px-3">
              No clients found. Please add some.
            </div>
          ) : (
            <>
              <div className="d-flex flex-wrap gap-2">
                {clientList.map((client) => (
                  <div
                    key={client.id}
                    className="d-flex align-items-center border rounded px-2 py-1 bg-light"
                    style={{
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      lineHeight: "1.5rem",
                      gap: "0.5rem",
                    }}
                  >
                    <input
                      className="form-check-input m-0"
                      type="checkbox"
                      checked={selectedClients.includes(client.slug)}
                      onChange={() => toggleClient(client.slug)}
                      id={`client-${client.id}`}
                      style={{ transform: "translateY(1px)" }}
                      disabled={recommendedClientSlugs.includes(
                        client.slug.toString()
                      )}
                    />

                    <label
                      className="form-check-label mb-0"
                      htmlFor={`client-${client.id}`}
                      style={{
                        cursor: "pointer",
                        userSelect: "none",
                        lineHeight: "1.5rem",
                      }}
                    >
                      {client.name}
                    </label>
                  </div>
                ))}
              </div>
              {errors.clients && (
                <div className="text-danger small mt-1">{errors.clients}</div>
              )}
            </>
          )}
        </div>

        {/* Subject */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Subject</label>
          <input
            type="text"
            className={`form-control ${errors.subject ? "is-invalid" : ""}`}
            placeholder="Enter subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          {errors.subject && (
            <div className="invalid-feedback">{errors.subject}</div>
          )}
        </div>

        {/* Content */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Content</label>
          <textarea
            className={`form-control ${errors.content ? "is-invalid" : ""}`}
            rows="5"
            placeholder="Write your message here..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          ></textarea>
          {errors.content && (
            <div className="invalid-feedback">{errors.content}</div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleModalClose}
          disabled={sending} // disable close button while sending
        >
          Close
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSend}
          disabled={sending} // disable send button while sending
        >
          {sending ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Sending...
            </>
          ) : (
            "Send Recommendation"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RecommendModal;
